import { Moment } from 'moment';
import React, { createContext, useContext, useRef, useState } from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { assertUnreachable } from 'src/lib/assertUnreachable';

/**
 * An intermediate representation of where the cursor is.
 * The expectation is that it's been translated from (x,y) coordinate space
 * to (date, yValue) data space for timeseries data or
 * (latencyIndex, yValue) for histograms.
 */
export type CursorPosition = {
  date?: Moment;
  latencyIndex?: number;
  yValue?: number;
} | null;

const CursorPositionContext = createContext<CursorPosition>(null);
const CursorUpdateContext = createContext<(cursor: CursorPosition) => void>(
  () => {},
);
export function CursorProvider({ children }: { children: React.ReactNode }) {
  const [cursor, setCursor] = useState<CursorPosition>(null);
  return (
    <CursorUpdateContext.Provider value={setCursor}>
      <CursorPositionContext.Provider value={cursor}>
        {children}
      </CursorPositionContext.Provider>
    </CursorUpdateContext.Provider>
  );
}
export const useCursorPosition = () => useContext(CursorPositionContext);
export const useCursorUpdate = () => useContext(CursorUpdateContext);

interface CursorProps {
  cursorValue:
    | { tooltipContent: React.ReactNode; x: number; y: number }
    | undefined
    | null;
  dotColor?: string | 'transparent';
  axisLine?: 'left' | 'right' | 'down';
  height?: number;
  width: number;
  showArrow?: boolean;
  showDot?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export const Cursor = ({
  cursorValue,
  dotColor = 'transparent',
  axisLine,
  width,
  height,
  showArrow = false,
  showDot,
  children,
  className,
}: CursorProps) => {
  const lastCursorValueRef = useRef(cursorValue);
  if (cursorValue) lastCursorValueRef.current = cursorValue;
  return (
    <Tooltip
      className={className}
      disabled={!cursorValue?.tooltipContent}
      label={lastCursorValueRef.current?.tooltipContent}
      hasArrow={showArrow}
      placement="top"
    >
      <g>
        {axisLine && (
          <line
            visibility={cursorValue ? 'visible' : 'hidden'}
            stroke={dotColor}
            strokeDasharray={4}
            {...(axisLine === 'left'
              ? {
                  x2: lastCursorValueRef.current?.x ?? 0,
                  y1: lastCursorValueRef.current?.y ?? 0,
                  y2: lastCursorValueRef.current?.y ?? 0,
                }
              : axisLine === 'right'
                ? {
                    x1: lastCursorValueRef.current?.x ?? 0,
                    x2: width,
                    y1: lastCursorValueRef.current?.y ?? 0,
                    y2: lastCursorValueRef.current?.y ?? 0,
                  }
                : axisLine === 'down'
                  ? {
                      x1: lastCursorValueRef.current?.x ?? 0,
                      x2: lastCursorValueRef.current?.x ?? 0,
                      y1: lastCursorValueRef.current?.y ?? 0,
                      y2: height,
                    }
                  : assertUnreachable(axisLine))}
          />
        )}
        {children ?? (
          <circle
            visibility={showDot || cursorValue ? 'visible' : 'hidden'}
            transform={`translate(${lastCursorValueRef.current?.x ?? 0}, ${
              lastCursorValueRef.current?.y ?? 0
            })`}
            r={4}
            fill={dotColor}
            stroke={dotColor}
          />
        )}
      </g>
    </Tooltip>
  );
};
